import React, { useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import { PageContainer, Poppins, Spacer, TableSkeleton } from 'src/common';
import Button from 'src/components/form/Button';
import { useAuth } from 'src/state/auth';
import { Invites, Requests } from './comps';
import { OrganisationCreateModalV2, WorkspaceCreateModal } from 'src/components/modals';
import { useCache } from 'src/graphql/useCache';
import OrgTable from 'src/components/OrgTable';
import Input from 'src/components/form/Input';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { useFlatOrganisationsHomeQuery } from 'src/graphql/_generated';

const Div = styled(PageContainer)`
  .head {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    grid-gap: 10px;

    &__buttons {
      display: flex;
      gap: 14px;
      grid-gap: 24px;
      align-items: center;

      &__switch {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-left: auto;
        cursor: pointer;
        user-select: none;
        margin-right: 10px;
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .head {
      &__buttons {
        flex-direction: column;
        gap: 14px;
        &__switch {
          margin-right: 0;
        }
      }
    }
  }
`;

const OrganisationHierarchyPage = () => {
  const [isOrganisationCreateModal, setIsOrganisationCreateModal] = useState(false);
  const [isWorspaceCreateModal, setIsWorspaceCreateModal] = useState(false);
  const [showArchived, setShowArchived] = useLocalStorage('showArchived', false);
  const { profile } = useAuth();
  const { refetchQuery } = useCache();
  useGaEventPageView('Cydea | Organisations');

  const {
    data: flatOrgsData,
    loading,
    previousData,
  } = useFlatOrganisationsHomeQuery({ variables: { includeArchived: showArchived } });
  const displayData = flatOrgsData ?? previousData;
  const flatOrganisations = displayData?.flatOrganisations || [];
  const isFlatOrgsLoading = loading && !displayData;

  const resetCache = () => refetchQuery({ include: ['profile', 'flatOrganisationsHome', 'flatOrganisationsNav'] });

  const canCreateChild = profile?.permissions.canCreateChild;

  return (
    <>
      <Meta title={`Cydea | Organisations`} feedbackScript />
      {isOrganisationCreateModal && <OrganisationCreateModalV2 onClose={() => setIsOrganisationCreateModal(false)} />}
      {isWorspaceCreateModal && <WorkspaceCreateModal onClose={() => setIsWorspaceCreateModal(false)} />}
      <Div>
        <div className="head">
          <Poppins px={32} weight={600}>
            Organisations
          </Poppins>

          <div className="head__buttons">
            <div
              className="head__buttons__switch"
              onClick={() => !loading && setShowArchived(!showArchived)}
              style={{
                cursor: loading ? 'not-allowed' : undefined,
                opacity: loading ? 0.7 : undefined,
                transition: 'opacity 0.25s ease',
              }}
            >
              <Input checked={showArchived} className="switch" type="checkbox" disabled={loading} />
              <Poppins color="davysGrey">Show archived workspaces</Poppins>
            </div>
            {(profile?.permissions.canCreateOrganisation || canCreateChild) && (
              <Button
                secondary
                onClick={() => setIsOrganisationCreateModal(true)}
                $constWidth={300}
                data-cy="org-create-btn"
              >
                + CREATE ORGANISATION
              </Button>
            )}
            {(profile?.permissions.canCreateWorkspace || canCreateChild) && (
              <Button secondary onClick={() => setIsWorspaceCreateModal(true)} $constWidth={300}>
                + CREATE WORKSPACE
              </Button>
            )}
          </div>
        </div>

        <Invites onSuccess={resetCache} />
        <Requests onSuccess={resetCache} />
        <Spacer $px={32} />
        {isFlatOrgsLoading ? <TableSkeleton /> : <OrgTable data={flatOrganisations} />}
      </Div>
    </>
  );
};

export default OrganisationHierarchyPage;
